export type SupportedRuntimeLocale = 'zh-CN' | 'en-US';

/**
 * 这个地方, 如果用户进去后带了 lang, 就先存在sessionStorage里, 避免之后跳转, 丢失了lang
 * 然后又说是bug....
 */
export const RUNTIME_LANG_QUERY_KEY = 'lang';
export const RUNTIME_LANG_SESSION_KEY = 'jfoto_runtime_lang';
export const RUNTIME_LANG_LOCAL_KEY = 'lang';


/**
 * 规范化运行时语言输入，输出受支持的语言或空字符串。
 * - 支持的输入形式包括但不限于：'en', 'en-US', 'EN_us' 都会被规范化为 'en-US'；'zh', 'cn', 'zh-CN', 'ZH_cn' 都会被规范化为 'zh-CN'。
 * - 不支持的输入会被规范化为 ''，调用方可以据此判断是否需要使用默认语言。
 *
 * 目的是为了识别 url上带了 lang=en|EN|en-US|en_us|zh|cn|zh-CN|ZH_cn
 * @param locale
 */
export const normalizeRuntimeLocale = (
  locale?: string | null
): SupportedRuntimeLocale | '' => {
  const raw = String(locale || '').trim().toLowerCase();

  if (!raw) {
    return '';
  }

  if (raw === 'en' || raw === 'en-us' || raw.indexOf('en') === 0) {
    return 'en-US';
  }

  if (
    raw === 'zh' ||
    raw === 'zh-cn' ||
    raw === 'cn' ||
    raw.indexOf('zh') === 0 ||
    raw.indexOf('cn') === 0
  ) {
    return 'zh-CN';
  }

  return '';
};

/**
 * 运行时语言来源说明：
 * - query `?lang=`：临时强制语言，优先级最高 👍👍👍
 * - sessionStorage.jfoto_runtime_lang：同 tab 内跳转时沿用上一跳的强制语言 👍👍
 * - localStorage.lang：历史版本里“显式切换语言”的持久化结果 👍
 *
 * 这样既兼容老系统，又能满足子相册等同 tab 跳转场景里，运行时强制语言持续生效。
 */
const getSafeSessionStorage = (): Storage | null => {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      return window.sessionStorage;
    }
  } catch (error) {
    // ignore
  }

  return null;
};

const getSafeLocalStorage = (): Storage | null => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      return window.localStorage;
    }
  } catch (error) {
    // ignore
  }

  return null;
};

export const getQueryRuntimeLocale = (
  search?: string
): SupportedRuntimeLocale | '' => {
  try {
    const targetSearch = typeof search === 'string' ? search : window.location.search;
    const params = new URLSearchParams(targetSearch || '');
    return normalizeRuntimeLocale(params.get(RUNTIME_LANG_QUERY_KEY));
  } catch (error) {
    return '';
  }
};

export const getSessionRuntimeLocale = (): SupportedRuntimeLocale | '' => {
  const storage = getSafeSessionStorage();
  if (!storage) {
    return '';
  }

  return normalizeRuntimeLocale(storage.getItem(RUNTIME_LANG_SESSION_KEY));
};

export const getLocalStorageRuntimeLocale = (): SupportedRuntimeLocale | '' => {
  const storage = getSafeLocalStorage();
  if (!storage) {
    return '';
  }

  return normalizeRuntimeLocale(storage.getItem(RUNTIME_LANG_LOCAL_KEY));
};

export const setSessionRuntimeLocale = (locale?: string | null): void => {
  const storage = getSafeSessionStorage();
  if (!storage) {
    return;
  }

  const normalizedLocale = normalizeRuntimeLocale(locale);
  if (normalizedLocale) {
    storage.setItem(RUNTIME_LANG_SESSION_KEY, normalizedLocale);
  } else {
    storage.removeItem(RUNTIME_LANG_SESSION_KEY);
  }
};

export const getPreferredRuntimeLocale = (): SupportedRuntimeLocale | '' => {
  const queryLocale = getQueryRuntimeLocale();
  if (queryLocale) {
    setSessionRuntimeLocale(queryLocale);
    return queryLocale;
  }

  const sessionLocale = getSessionRuntimeLocale();
  if (sessionLocale) {
    return sessionLocale;
  }

  return getLocalStorageRuntimeLocale();
};

/**
 * 分享链接更关注“当前这一跳用户正在看的语言”，因此保留 session 作为第二优先级，
 * 用来承接 query 强制语言在同 tab 内的后续跳转。
 */
export const getRuntimeLocaleForShare = (): SupportedRuntimeLocale | '' => {
  return (
    getQueryRuntimeLocale() ||
    getSessionRuntimeLocale() ||
    getLocalStorageRuntimeLocale()
  );
};
